import React, { createContext, useContext, useState } from 'react';
import CustomModal from '../components/common/CustomModal';

const ModalContext = createContext();

export const useModal = () => useContext(ModalContext);

export const ModalProvider = ({ children }) => {
  const [modalState, setModalState] = useState({
    open: false,
    title: '',
    message: '',
    type: 'info', // 'success', 'error', 'warning', 'info', 'confirm'
    actions: [],
    showCloseButton: true,
  });

  const closeModal = () => {
    setModalState(prev => ({ ...prev, open: false }));
  };
  // Success modal
  const openSuccessModal = (title = 'Success', message='Submitted successfully', onConfirm) => {
    setModalState({
      open: true,
      title,
      message,
      type: 'success',
      actions: [
        {
          label: 'OK',
          onClick: onConfirm ? () => {
            onConfirm();
            closeModal();
          } : closeModal,
          color: 'primary',
          variant: 'contained',
        },
      ],
      showCloseButton: true,
    });
  };

  // Error modal
  const openErrorModal = (title = 'Error', message='Something went wrong') => {
    setModalState({
      open: true,
      title,
      message,
      type: 'error',
      actions: [
        {
          label: 'OK',
          onClick: closeModal,
          color: 'error',
          variant: 'contained',
        },
      ],
      showCloseButton: true,
    });
  };

  // Warning modal
  const openWarningModal = (title = 'Warning', message='') => {
    setModalState({
      open: true,
      title,
      message,
      type: 'warning',
      actions: [
        {
          label: 'OK',
          onClick: closeModal,
          color: 'warning',
          variant: 'contained',
        },
      ],
      showCloseButton: true,
    });
  };

  // Confirmation modal
  const openConfirmModal = (title = 'Confirm', message='Are you sure you want to proceed?', onConfirm, confirmLabel = 'Confirm') => {
    setModalState({
      open: true,
      title,
      message,
      type: 'confirm',
      actions: [
        {
          label: 'Cancel',
          onClick: closeModal,
          color: 'inherit',
          variant: 'outlined',
        },
        {
          label: confirmLabel,
          onClick: () => {
            onConfirm();
            closeModal();
          },
          color: 'primary',
          variant: 'contained',
        },
      ],
      showCloseButton: false,
    });
  };

  // No changes detected modal
  const openNoChangesModal = (title = 'No Changes', message='No changes were detected in the form.') => {
    setModalState({
      open: true,
      title,
      message,
      type: 'info',
      actions: [
        {
          label: 'OK',
          onClick: closeModal,
          color: 'primary',
        },
      ],
      showCloseButton: true,
    });
  };

  // HTTP error handler
  const handleHttpError = (error) => {
    const status = error?.response?.status;
    const message = error?.response?.data?.message || error.message;

    switch (status) {
      case 400:
        openErrorModal('The request was invalid. Please check your input and try again.', 'Bad Request');
        break;
      case 401:
        openErrorModal('Your session has expired. Please log in again.', 'Session Expired');
        // Optionally trigger logout here
        break;
      case 403:
        openErrorModal('You do not have permission to perform this action.', 'Access Denied');
        break;
      case 404:
        openErrorModal('The requested resource was not found.', 'Not Found');
        break;
      case 500:
        openErrorModal('An internal server error occurred. Please try again later.', 'Server Error');
        break;
      default:
        openErrorModal(message || 'An unexpected error occurred.', 'Error');
    }
  };

  return (
    <ModalContext.Provider
      value={{
        openSuccessModal,
        openErrorModal,
        openWarningModal,
        openConfirmModal,
        openNoChangesModal,
        handleHttpError,
      }}
    >
      {children}
      <CustomModal
        open={modalState.open}
        onClose={closeModal}
        title={modalState.title}
        message={modalState.message}
        type={modalState.type}
        actions={modalState.actions}
        showCloseButton={modalState.showCloseButton}
      />
    </ModalContext.Provider>
  );
};

export default ModalContext;
